/**
 * @file DocParagraph.tsx
 * @brief TipTap rich-text paragraph that reads as Times MLR body on the paper.
 *
 * @details
 * Replaces the plain textarea so doctors can bold / italic / underline / list
 * like Word. HTML is stored on save; PDF export strips markup
 * (`stripMlrMarkup`) (08-09-2026).
 *
 * @author DES
 * @date 08-09-2026
 */

import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "@/shared/theme/cn";
import { normalizeRichHtml, toEditorHtml } from "../mlrRichText";
import { useMlrRichText } from "./mlrRichTextContext";

type Props = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  dirty?: boolean;
  minRows?: number;
};

export function DocParagraph({
  value,
  onChange,
  ariaLabel,
  placeholder,
  dirty = false,
  minRows = 2,
}: Props) {
  const { setFocusedEditor, clearIfFocused } = useMlrRichText();
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
      }),
      Underline,
      Placeholder.configure({
        placeholder: placeholder ?? "",
      }),
    ],
    content: toEditorHtml(value),
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        class: "mlr-rte outline-none",
        spellcheck: "true",
      },
    },
    onFocus: ({ editor: instance }) => {
      setFocusedEditor(instance);
    },
    onBlur: ({ editor: instance, event }) => {
      const related = event.relatedTarget;
      if (
        related instanceof HTMLElement &&
        related.closest("[data-mlr-format-toolbar]")
      ) {
        return;
      }
      clearIfFocused(instance);
    },
    onUpdate: ({ editor: instance }) => {
      const html = instance.getHTML();
      if (normalizeRichHtml(html) === normalizeRichHtml(valueRef.current)) {
        return;
      }
      onChangeRef.current(html);
    },
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const next = toEditorHtml(value);
    if (normalizeRichHtml(editor.getHTML()) === normalizeRichHtml(next)) {
      return;
    }
    editor.commands.setContent(next, false);
  }, [editor, value]);

  useEffect(() => {
    return () => {
      clearIfFocused(editor);
    };
  }, [editor, clearIfFocused]);

  return (
    <div
      style={{
        fontFamily: '"Times New Roman", Times, Georgia, serif',
        minHeight: minRows * 22,
      }}
      className={cn(
        "mlr-rte-shell mt-1 px-0 py-0.5",
        dirty && "bg-[#fffbeb]/50",
      )}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
